import React from 'react';
import { Redirect, Route, RouteProps } from 'react-router-dom';

import { getAccessToken } from '@nw/shared/data-access';

import { SeoDetails, SeoTags } from '../SeoTags';

interface ProtectedRouteProps extends RouteProps {
  component: React.FC<any>;
  getRouteSeoInfo?: (pathname: string) => SeoDetails | undefined;
}

/**
 * A route wrapper that protects routes and redirects unauthenticated users to the login page,
 * preserving all query parameters.
 *
 * @param {React.ComponentType<any>} component - The component to render if the user is authenticated.
 * @param {...RouteProps} rest - The remaining route props passed to the `Route` component.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps & RouteProps> = ({
  component: Component,
  getRouteSeoInfo,
  ...other
}) => {
  const isLoggedIn = getAccessToken() !== undefined;

  return (
    <Route
      {...other}
      render={({ location, ...props }) => {
        const seoInfo = getRouteSeoInfo?.(location.pathname);

        return (
          <>
            {seoInfo ? <SeoTags seoInfo={seoInfo} /> : null}
            {isLoggedIn ? (
              <Component {...props} />
            ) : (
              <Redirect
                to={{
                  pathname: '/login',
                  search: location.search,
                  state: { from: location }
                }}
              />
            )}
          </>
        );
      }}
    />
  );
};

export default ProtectedRoute;
